import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { Table, Typography, Modal, Button, Input, DatePicker, Select, Col, Row, Space, Dropdown, Menu, Spin, Card, Badge, Tag, Timeline } from "antd";
import ViewOneEmployeeRecord from "./hr/ViewOneEmployeeRecord";
import { CheckOutlined, CloseOutlined, DownOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import ViewOneEmpPerm from "./ViewOneEmpPerm";
import ViewEmpDetails from "./ViewEmpDetails";
const { Text, Title } = Typography;

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function TeamPermissions() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const { authData, backUrl } = useContext(AuthContext);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showSingle, setShowSingle] = useState(false);
    const [showRejectReason, setShowRejectReason] = useState({ status: false, data: null });
    const [showAcceptReason, setShowAcceptReason] = useState({ status: false, data: null });
    const [rejectionData, setRejectionData] = useState("");
    const [approvalData, setApprovalData] = useState("");

    // Loading states
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);

    // Bulk Action States
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showBulkApprove, setShowBulkApprove] = useState(false);
    const [showBulkReject, setShowBulkReject] = useState(false);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkProcessing, setBulkProcessing] = useState(false);

    // Unapproved permissions state
    const [unapprovedPermissions, setUnapprovedPermissions] = useState([]);
    const [isUnapproved, setIsUnapproved] = useState(false);
    const [isHod, setIsHod] = useState(false);

    // Filters
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [empNameFilter, setEmpNameFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState([]);
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("Pending");

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    useEffect(() => {
        if (authData?.employee?.is_hod === true) {
            setIsHod(true);
        }
    }, [authData]);

    const fetchUnapproved = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/hr/unapproved-permissions/?is_employee_view=true`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            });
            setUnapprovedPermissions(res.data.results || []);
            console.log("Unapproved Permissions: ", res.data.results);
        } catch (error) {
            console.error("Error fetching unapproved permissions:", error);
        }
    };

    useEffect(() => {
        fetchUnapproved();
    }, []);

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || []);
    };

    const fetchDepartments = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results || []);
    };

    const fetchDesignations = async (deptId) => {
        const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDesignations(res.data.results || []);
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    const fetchData = async () => {
        try {
            const params = new URLSearchParams();
            params.append("is_employee_view", "true");

            if (approvalStatusFilter && !isUnapproved) {
                const statusMapping = {
                    "Pending": ["Pending", "Pending Cancellation", "Pending Approval"],
                    "Approved": ["Approved", "Cancellation Approved"],
                    "Rejected": ["Rejected", "Cancellation Rejected"]
                };

                const allowedStatuses =
                    statusMapping[approvalStatusFilter] || [approvalStatusFilter];

                // 👉 Convert to comma-separated string
                params.append("status", allowedStatuses.join(","));
            }

            const res = await axios.get(
                `${backUrl}/api/hr/permissions/?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const results = res.data.results || [];
            setData(results);

        } catch (error) {
            console.error("Error fetching permissions data:", error);
            toast.error("Failed to fetch permissions data");
        }
    };

    // ⭐ Updated Filter Logic with Dual Status Support
    // Remove the status part from applyFilters
    const applyFilters = () => {
        let temp = isUnapproved ? [...unapprovedPermissions] : [...data];

        if (empCodeFilter) {
            temp = temp.filter((item) =>
                item.employee?.user?.username?.toLowerCase().includes(empCodeFilter.toLowerCase())
            );
        }

        if (empNameFilter) {
            temp = temp.filter((item) =>
                item.employee?.user?.first_name?.toLowerCase().includes(empNameFilter.toLowerCase())
            );
        }

        if (organisationFilter) {
            temp = temp.filter(item => item.employee?.organisation_name === organisationFilter);
        }

        if (departmentFilter) {
            temp = temp.filter(item => item.employee?.department_name === departmentFilter);
        }

        if (designationFilter) {
            temp = temp.filter(item => item.employee?.designation_name === designationFilter);
        }

        if (dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            temp = temp.filter((item) => {
                const date = dayjs(item.date);
                return (
                    date.isAfter(dayjs(start).startOf("day").subtract(1, "ms")) &&
                    date.isBefore(dayjs(end).endOf("day").add(1, "ms"))
                );
            });
        }

        // ⭐ Status filtering removed — handled by backend via query params

        setFilteredData(temp);
    };

    // Add approvalStatusFilter to fetchData's useEffect dependency
    useEffect(() => {
        fetchData();
    }, [approvalStatusFilter, isUnapproved]);  // re-fetch when status changes

    useEffect(() => {
        applyFilters();
    }, [
        empCodeFilter,
        empNameFilter,
        dateRangeFilter,
        approvalStatusFilter,
        organisationFilter,
        departmentFilter,
        designationFilter,
        data,
        unapprovedPermissions,
        isUnapproved,
    ]);

    const cancellationMenu = (
        <Menu
            onClick={({ key }) => {
                setApprovalStatusFilter(key);
                setIsUnapproved(false);
            }}
        >
            <Menu.Item key="Pending Cancellation">
                Pending Cancellation
            </Menu.Item>
            <Menu.Item key="Cancellation Approved">
                Cancellation Approved
            </Menu.Item>
            <Menu.Item key="Cancellation Rejected">
                Cancellation Rejected
            </Menu.Item>
        </Menu>
    );

    const handleApprove = async (id) => {
        setApproveLoading(true);

        const approveApi = `${backUrl}/api/hr/permissions/approve/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-permissions/${id}/admin-update-status/`;

        let res;

        try {
            if (isUnapproved === true) {
                res = await axios.post(
                    unApprovedApi,
                    {
                        status: "Approved",
                        approver_reason: approvalData
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
            } else {
                res = await axios.post(
                    approveApi,
                    {
                        permission_ids: [id],
                        approver_reason: approvalData
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
            }

            const successMsg =
                res?.data?.message ||
                res?.data?.detail ||
                "Permission Approved Successfully";

            toast.success(successMsg);

            setApprovalData("");
            setShowAcceptReason({ status: false, data: null });

            await fetchData();
            await fetchUnapproved();

        } catch (error) {
            const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Approval failed";

            toast.error(errorMsg);
        } finally {
            setApproveLoading(false);
        }
    };

    const handleReject = async (id) => {
        setRejectLoading(true);

        const rejectApi = `${backUrl}/api/hr/permissions/reject/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-permissions/${id}/admin-update-status/`;

        let res;

        try {
            if (isUnapproved === true) {
                res = await axios.post(
                    unApprovedApi,
                    {
                        status: "Rejected",
                        rejection_reason: rejectionData
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
            } else {
                res = await axios.post(
                    rejectApi,
                    {
                        permission_ids: [id],
                        rejection_reason: rejectionData
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
            }

            const successMsg =
                res?.data?.message ||
                res?.data?.detail ||
                "Permission Rejected Successfully";

            toast.success(successMsg);

            setRejectionData("");
            setShowRejectReason({ status: false, data: null });

            await fetchData();
            await fetchUnapproved();

        } catch (error) {
            let errorMsg = "Rejection failed";

            const data = error?.response?.data;

            if (typeof data === "string") {
                errorMsg = data;
            } else if (data?.error) {
                errorMsg = data.error;
            } else if (data?.detail) {
                errorMsg = data.detail;
            } else if (typeof data === "object") {
                errorMsg = Object.values(data)
                    .flat()
                    .join(", ");
            }

            toast.error(errorMsg);
        } finally {
            setRejectLoading(false);
        }
    };

    // Bulk Approve Handler
    const handleBulkApprove = async () => {
        if (!bulkRemarks.trim()) {
            toast.error("Please enter remarks");
            return;
        }

        setBulkProcessing(true);

        try {
            const bulkApproveApi = `${backUrl}/api/hr/permissions/approve/?is_employee_view=true`;

            const payload = {
                permission_ids: selectedRowKeys,
                approver_reason: bulkRemarks
            };

            const res = await axios.post(bulkApproveApi, payload, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            toast.success(res.data?.message || "Permissions approved successfully");

            setBulkRemarks("");
            setShowBulkApprove(false);
            setSelectedRowKeys([]);

            await fetchData();
            await fetchUnapproved();

        } catch (error) {
            const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Bulk approval failed";
            toast.error(errorMsg);
        } finally {
            setBulkProcessing(false);
        }
    };

    // Bulk Reject Handler
    const handleBulkReject = async () => {
        if (!bulkRemarks.trim()) {
            toast.error("Please enter rejection reason");
            return;
        }

        setBulkProcessing(true);

        try {
            const bulkRejectApi = `${backUrl}/api/hr/permissions/reject/?is_employee_view=true`;

            const payload = {
                permission_ids: selectedRowKeys,
                rejection_reason: bulkRemarks
            };

            const res = await axios.post(bulkRejectApi, payload, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });

            toast.success(res.data?.message || "Permissions rejected successfully");

            setBulkRemarks("");
            setShowBulkReject(false);
            setSelectedRowKeys([]);

            await fetchData();
            await fetchUnapproved();

        } catch (error) {
            const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                "Bulk rejection failed";
            toast.error(errorMsg);
        } finally {
            setBulkProcessing(false);
        }
    };

    // Row Selection Configuration
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "user", "username"],
            key: "employee_code",
            render: (text, record) => (
                <a
                    onClick={() => {
                        setSelectedEmployee(record);
                        setShowSingle(true);
                    }}
                >
                    {text}
                </a>
            ),
            fixed: "left",
            width: 140,
        },
        {
            title: "Employee Name",
            key: "employee_name",
            width: 180,
            render: (record) => {
                const firstName = record?.employee?.user?.first_name || "";
                const lastName = record?.employee?.user?.last_name || "";
                return `${firstName} ${lastName}`.trim() || "-";
            },
        },
        {
            title: "Approve",
            key: "approve",
            width: 90,
            render: (_, record) => (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                    onClick={() => setShowAcceptReason({ status: true, data: record.id })}
                />
            ),
        },
        {
            title: "Reject",
            key: "reject",
            width: 90,
            render: (_, record) => (
                <Button
                    danger
                    type="primary"
                    shape="circle"
                    icon={<CloseOutlined />}
                    onClick={() => setShowRejectReason({ status: true, data: record.id })}
                />
            ),
        },
        { title: "Date", dataIndex: "date", key: "date", width: 130 },
        { title: "Session", dataIndex: "session", key: "session", width: 120 },
        { title: "Hours", dataIndex: "hours_requested", key: "hours_requested", width: 100 },
        {
            title: "Reason",
            key: "reason",
            width: 300,
            render: (_, record) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {record.reason || "-"}
                    <br />
                    <br />
                    {record.cancellation_reason && (
                        <div
                            style={{
                                marginTop: "8px",
                                fontWeight: 600,
                                color: "red",
                                backgroundColor: "#ffe6e6",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                display: "inline-block",
                            }}
                        >
                            Cancellation Reason: {record.cancellation_reason}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Status",
            key: "status",
            width: 130,
            render: (_, record) => {
                const status = isUnapproved ? record.status : record.approve_status;
                return (
                    <span
                        style={{
                            fontWeight: 500,
                            color:
                                status === "Approved" || status === "Cancellation Approved"
                                    ? "green"
                                    : status === "Rejected" || status === "Cancellation Rejected"
                                        ? "red"
                                        : "#faad14",
                        }}
                    >
                        {status}
                    </span>
                );
            },
        },
        {
            title: "Approval Hierarchy",
            key: "remarks",
            width: 450,
            render: (_, record) => {
                if (isUnapproved) {
                    return record.remarks || "-";
                }
                const approvals = record.approvals || [];
                if (!approvals.length) return "-";

                return (
                    <div style={{ marginTop: "16px" }}>
                        <Timeline
                            items={approvals.map((a, index) => {
                                const isRejected = !!(a.rejected_on || a.cancellation_rejected_on);
                                const isApproved = !!(a.approved_on || a.cancellation_approved_on);
                                const timelineColor = isRejected ? "red" : isApproved ? "green" : "gray";

                                return {
                                    color: timelineColor,
                                    children: (
                                        <div style={{ marginBottom: "8px" }}>
                                            {/* Name and Status Tag */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                                                <Text strong style={{ fontSize: "15px" }}>
                                                    {a.approver?.first_name || a.approver?.last_name
                                                        ? `${a.approver?.first_name} ${a.approver?.last_name}`
                                                        : a.approver?.user?.username}
                                                </Text>
                                                <Tag color={isRejected ? "error" : isApproved ? "success" : "default"} style={{ margin: 0 }}>
                                                    {a.status}
                                                </Tag>
                                            </div>

                                            {/* Designation */}
                                            {a.approver?.designation_name && (
                                                <div style={{ color: "#8c8c8c", fontSize: "13px", marginTop: "2px" }}>
                                                    {a.approver.designation_name}
                                                </div>
                                            )}

                                            {/* On Date/Time */}
                                            {(a.approved_on || a.rejected_on || a.cancellation_approved_on || a.cancellation_rejected_on) && (
                                                <div style={{ color: "#595959", fontSize: "12px", marginTop: "4px" }}>
                                                    On: {dayjs(a.approved_on || a.rejected_on || a.cancellation_approved_on || a.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                                                </div>
                                            )}

                                            {/* Action Remarks */}
                                            <div style={{ marginTop: "6px", fontSize: "13px" }}>
                                                {!a.status?.includes("Cancellation") ? (
                                                    <>
                                                        {a.status === "Approved" ? (
                                                            <div style={{ marginBottom: "2px" }}>
                                                                <Text strong>Remark: </Text>
                                                                <Text>{a.approver_reason || "Approved"}</Text>
                                                            </div>
                                                        ) : a.status === "Rejected" ? (
                                                            <div style={{ marginBottom: "2px" }}>
                                                                <Text strong>Remark: </Text>
                                                                <Text>{a.rejection_reason || a.approver_reason || "Rejected"}</Text>
                                                            </div>
                                                        ) : null}
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* For cancellation actions */}
                                                        {a.approver_reason && (
                                                            <div style={{ marginBottom: "2px" }}>
                                                                <Text strong>Remark: </Text>
                                                                <Text>{a.approver_reason}</Text>
                                                            </div>
                                                        )}
                                                        {a.status === "Cancellation Approved" ? (
                                                            <div style={{ marginBottom: "2px" }}>
                                                                <Text strong>Cancellation Remark: </Text>
                                                                <Text>{a.cancellation_reason || "Cancellation Approved"}</Text>
                                                            </div>
                                                        ) : a.status === "Cancellation Rejected" ? (
                                                            <div style={{ marginBottom: "2px" }}>
                                                                <Text strong>Cancellation Remark: </Text>
                                                                <Text>{a.cancellation_rejected_reason || a.cancellation_reason || "Cancellation Rejected"}</Text>
                                                            </div>
                                                        ) : null}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ),
                                };
                            })}
                        />
                    </div>
                );
            },
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name" },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name" },

    ];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <center>
                <Title level={5}>Team Permissions</Title>
            </center>

            {/* Status Filter Buttons */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button
                    type={approvalStatusFilter === "Pending" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Pending");
                        setIsUnapproved(false);
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                        borderColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                    }}
                >
                    Pending
                </Button>
                <Button
                    type={approvalStatusFilter === "Approved" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Approved");
                        setIsUnapproved(false);
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                        borderColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                    }}
                >
                    Approved
                </Button>
                <Button
                    type={approvalStatusFilter === "Rejected" ? "primary" : "default"}
                    onClick={() => {
                        setApprovalStatusFilter("Rejected");
                        setIsUnapproved(false);
                    }}
                    style={{
                        marginRight: "10px",
                        backgroundColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                        borderColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                    }}
                >
                    Rejected
                </Button>

                {isHod && (
                    <Button
                        type={isUnapproved ? "primary" : "default"}
                        onClick={() => {
                            setApprovalStatusFilter("Unapproved");
                            setIsUnapproved(true);
                        }}
                        style={{
                            backgroundColor: isUnapproved ? "blue" : undefined,
                            borderColor: isUnapproved ? "blue" : undefined,
                        }}
                    >
                        Unapproved Permissions
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div style={{ marginBottom: "16px" }}>
                <Row gutter={[12, 12]} justify="center">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Employee Code"
                            value={empCodeFilter}
                            onChange={(e) => setEmpCodeFilter(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Employee Name"
                            value={empNameFilter}
                            onChange={(e) => setEmpNameFilter(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: "100%" }}
                            value={organisationFilter || undefined}
                            allowClear
                            onChange={(value) => {
                                setOrganisationFilter(value);
                                setDepartmentFilter("");
                                setDesignationFilter("");
                                setDepartments([]);
                                setDesignations([]);

                                if (value) {
                                    const orgObj = Organisations.find(o => o.name === value);
                                    if (orgObj) fetchDepartments(orgObj.id);
                                }
                            }}
                        >
                            {Organisations.map(org => (
                                <Option key={org.id} value={org.name}>{org.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={departmentFilter || undefined}
                            allowClear
                            disabled={!organisationFilter}
                            onChange={(value) => {
                                setDepartmentFilter(value);
                                setDesignationFilter("");
                                setDesignations([]);

                                if (value) {
                                    const deptObj = departments.find(d => d.name === value);
                                    if (deptObj) fetchDesignations(deptObj.id);
                                }
                            }}
                        >
                            {departments.map(dept => (
                                <Option key={dept.id} value={dept.name}>{dept.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={designationFilter || undefined}
                            allowClear
                            disabled={!departmentFilter}
                            onChange={(value) => setDesignationFilter(value)}
                        >
                            {designations.map(des => (
                                <Option key={des.id} value={des.name}>{des.name}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <RangePicker
                            value={dateRangeFilter}
                            onChange={(dates) => setDateRangeFilter(dates || [])}
                            style={{ width: "100%" }}
                        />
                    </Col>
                </Row>
            </div>

            {/* Bulk Action Buttons */}
            {selectedRowKeys.length > 0 && (
                <div style={{ margin: "20px 0", textAlign: "center" }}>
                    <Space>
                        <span style={{ fontWeight: "500" }}>
                            {selectedRowKeys.length} item{selectedRowKeys.length > 1 ? "s" : ""} selected
                        </span>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                            onClick={() => setShowBulkApprove(true)}
                        >
                            Bulk Approve
                        </Button>
                        <Button
                            danger
                            type="primary"
                            icon={<CloseOutlined />}
                            onClick={() => setShowBulkReject(true)}
                        >
                            Bulk Reject
                        </Button>
                        <Button onClick={() => setSelectedRowKeys([])}>
                            Clear Selection
                        </Button>
                    </Space>
                </div>
            )}

            {/* Scrollable Table */}
            <div style={{ overflowX: "auto" }}>
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    rowSelection={rowSelection}
                    scroll={{ x: 2500 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{ backgroundColor: "#0e497b", color: "white" }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            </div>

            {/* Individual Reject Modal */}
            <Modal
                title="Reason for Rejection"
                open={showRejectReason.status}
                footer={null}
                onCancel={() => setShowRejectReason({ status: false, data: null })}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason here..."
                    value={rejectionData}
                    onChange={(e) => setRejectionData(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => setShowRejectReason({ status: false, data: null })}
                        disabled={rejectLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        danger
                        onClick={() => handleReject(showRejectReason.data)}
                        loading={rejectLoading}
                    >
                        {rejectLoading ? "Rejecting..." : "Reject"}
                    </Button>
                </div>
            </Modal>

            {/* Individual Approval Modal */}
            <Modal
                title="Approval Remarks"
                open={showAcceptReason.status}
                footer={null}
                onCancel={() => setShowAcceptReason({ status: false, data: null })}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter remarks here..."
                    value={approvalData}
                    onChange={(e) => setApprovalData(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => setShowAcceptReason({ status: false, data: null })}
                        disabled={approveLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                        onClick={() => handleApprove(showAcceptReason.data)}
                        loading={approveLoading}
                    >
                        {approveLoading ? "Approving..." : "Approve"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal
                title={`Bulk Approve (${selectedRowKeys.length} permissions)`}
                open={showBulkApprove}
                footer={null}
                onCancel={() => {
                    setShowBulkApprove(false);
                    setBulkRemarks("");
                }}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter common remarks for all selected permissions..."
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => {
                            setShowBulkApprove(false);
                            setBulkRemarks("");
                        }}
                        disabled={bulkProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={bulkProcessing}
                        disabled={bulkProcessing || !bulkRemarks.trim()}
                        style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                        onClick={handleBulkApprove}
                    >
                        {bulkProcessing ? "Approving..." : "Approve All"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal
                title={`Bulk Reject (${selectedRowKeys.length} permissions)`}
                open={showBulkReject}
                footer={null}
                onCancel={() => {
                    setShowBulkReject(false);
                    setBulkRemarks("");
                }}
                width={600}
                centered
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter common rejection reason for all selected permissions..."
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => {
                            setShowBulkReject(false);
                            setBulkRemarks("");
                        }}
                        disabled={bulkProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        danger
                        loading={bulkProcessing}
                        disabled={bulkProcessing || !bulkRemarks.trim()}
                        onClick={handleBulkReject}
                    >
                        {bulkProcessing ? "Rejecting..." : "Reject All"}
                    </Button>
                </div>
            </Modal>

            {/* Employee Record Modal */}
            <Modal
                open={showSingle} footer={null} onCancel={() => setShowSingle(false)} width={800}
            >
                <ViewEmpDetails employee={selectedEmployee} />
            </Modal>
        </>
    );
}